import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { SortFilterType } from 'components'
import { toast } from 'react-toastify'
import { getNDList } from 'services/nd'
import type { NDPayload } from 'services/nd/interface'
import type { AppThunk } from 'store/store'

import type { Negotiation, Pagination, Statistics } from './Types/NegotiationType'

type InitialState = {
  loadingNDs: boolean
  loadingReport: boolean
  negotiationsList: Negotiation[]
  negotiationsStatistics: Statistics
  sortFilter: SortFilterType
  pagination: Pagination
}
const initialState: InitialState = {
  loadingNDs: true,
  loadingReport: false,
  negotiationsList: [],
  negotiationsStatistics: {} as Statistics,
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  },
  sortFilter: {} as SortFilterType,
}

const negotiations = createSlice({
  initialState,
  name: 'negotiations',
  reducers: {
    afterRequest(state) {
      state.loadingNDs = false
    },
    onRequest(state) {
      state.loadingNDs = true
    },
    resetPagination(state) {
      state.pagination = {
        currentPage: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0,
      }
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.pagination.currentPage = action.payload
    },
    setLoadingReport(state, action: PayloadAction<boolean>) {
      state.loadingReport = action.payload
    },
    setNegotiationStatiscs(state, action: PayloadAction<Statistics>) {
      state.negotiationsStatistics = action.payload
    },
    setNegotiationsList(state, action: PayloadAction<Negotiation[] | undefined>) {
      if (action.payload) {
        state.negotiationsList = action.payload
      } else {
        state.negotiationsList = []
      }
    },
    setPagination(state, action: PayloadAction<Pagination>) {
      state.pagination = action.payload
    },
    setSortFilter(state, action: PayloadAction<SortFilterType>) {
      state.sortFilter = action.payload
    },
  },
})

export const negotiationsContext = negotiations.reducer
export const {
  onRequest,
  afterRequest,
  setLoadingReport,
  setNegotiationsList,
  setNegotiationStatiscs,
  setSortFilter,
  setPagination,
  setCurrentPage,
  resetPagination,
} = negotiations.actions

export const asyncGetNegotiations =
  (payload: NDPayload, page: number, sortFilter: SortFilterType): AppThunk =>
  async (dispatch, getState) => {
    dispatch(onRequest())
    const { pagination } = getState().negotiations
    const pageSize = 10
    try {
      const negotiationsData = await getNDList(payload, {
        limit: pageSize,
        page: page - 1,
        ...sortFilter,
      })

      const hasNegotiations =
        Object.keys(negotiationsData).includes('NegociacoesDiferenciadasAgrupadas') &&
        negotiationsData.NegociacoesDiferenciadasAgrupadas.length > 0

      if (hasNegotiations) {
        const { NegociacoesDiferenciadasAgrupadas: negotiationsList, ...Statistics } =
          negotiationsData

        const totalItems = negotiationsList[0].RowsCount
        const totalPages = Math.ceil(totalItems / pageSize)
        const updatedPagination = {
          ...pagination,
          currentPage: page,
          pageSize,
          totalItems,
          totalPages,
        }

        dispatch(setPagination(updatedPagination))
        dispatch(setNegotiationsList(negotiationsList))
        dispatch(setNegotiationStatiscs(Statistics))
      } else {
        dispatch(setPagination({ ...pagination, currentPage: 1 }))
        dispatch(setNegotiationsList())
        dispatch(setNegotiationStatiscs({} as Statistics))
      }
    } catch (error) {
      toast.error('Erro ao obter negociações.')
    } finally {
      dispatch(afterRequest())
    }
  }
